import React, { useMemo, useState } from 'react';
import {
    Box,
    ButtonBase,
    Chip,
    Link,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
    alpha,
    useTheme,
} from '@mui/material';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import {
    EmptyNote,
    FilledWidget,
    Panel,
    SectionRow,
    WidgetRow,
    findWidget,
    widgetRows,
} from './schoolDashboard';

/**
 * ACADEMIC IMPACT -- the weekly accuracy trend, and what is behind it.
 *
 * Three widgets feed it: `academic_impact` (whole school), and the same weeks
 * split `academic_impact_by_subject` and `academic_impact_by_section`. The
 * chart shows one line; the list under it shows every subject or section with
 * its own change, and clicking one draws that line instead of the school's.
 *
 * A week counts only once it has MIN_WEEK_ANSWERED answers. Below that one
 * lucky quiz reads as a 20-point swing, which is noise and not a trend.
 */

const MIN_WEEK_ANSWERED = 20;

interface Point {
    week: string;
    pct: number | null;
    answered: number;
}

interface Series {
    key: string;
    name: string;
    points: Point[];
    latest: number | null;
    change: number | null;
    answered: number;
}

type Split = 'subject' | 'section';

const shortWeek = (iso: string) => {
    const d = new Date(`${iso}T00:00:00`);
    return isNaN(d.getTime())
        ? iso
        : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
};

const num = (v: WidgetRow[string]) => (v === null || v === undefined ? null : Number(v));

/** One line, aligned to the shared week axis. */
function buildSeries(key: string, name: string, rows: WidgetRow[], weeks: string[]): Series {
    const byWeek = new Map(rows.map(r => [String(r.week_start), r]));
    const points = weeks.map(week => {
        const r = byWeek.get(week);
        const answered = r ? num(r.questions_attempted) || 0 : 0;
        const pct = r && answered >= MIN_WEEK_ANSWERED ? num(r.accuracy_pct) : null;
        return { week, pct, answered };
    });
    const known = points.filter(p => p.pct !== null).map(p => p.pct as number);
    const latest = known.length ? known[known.length - 1] : null;
    const change = known.length > 1 ? Math.round((known[known.length - 1] - known[0]) * 10) / 10 : null;
    return {
        key,
        name,
        points,
        latest,
        change,
        answered: points.reduce((s, p) => s + p.answered, 0),
    };
}

const changeLabel = (c: number) => `${c >= 0 ? '+' : ''}${c} pts`;

/**
 * The main chart. The line is SVG stretched to the width; the dots and week
 * labels are HTML laid over it, so they stay round and readable at any width.
 */
const TrendChart = ({ series, height = 150 }: { series: Series; height?: number }) => {
    const theme = useTheme();
    const color = theme.palette.success.main;
    const pad = { top: 14, bottom: 8 };

    const drawn = useMemo(() => {
        const n = series.points.length;
        const valid = series.points
            .map((p, i) => ({ ...p, i }))
            .filter(p => p.pct !== null) as Array<Point & { i: number; pct: number }>;
        if (valid.length < 2) return null;

        const values = valid.map(v => v.pct);
        // Pad the scale a little so a flat line does not sit on the floor.
        const min = Math.max(0, Math.floor(Math.min(...values) - 3));
        const max = Math.min(100, Math.ceil(Math.max(...values) + 3));
        const span = max - min || 1;
        const xPct = (i: number) => (n === 1 ? 50 : (i / (n - 1)) * 100);
        const yPx = (v: number) => pad.top + (1 - (v - min) / span) * (height - pad.top - pad.bottom);

        const dots = valid.map(v => ({ ...v, x: xPct(v.i), y: yPx(v.pct) }));
        const line = dots.map(d => `${d.x.toFixed(2)},${d.y.toFixed(2)}`).join(' ');
        return {
            dots,
            line,
            area: `${dots[0].x.toFixed(2)},${height} ${line} ${dots[dots.length - 1].x.toFixed(2)},${height}`,
            min,
            max,
            xPct,
        };
    }, [series, height, pad.top, pad.bottom]);

    if (!drawn) {
        return <EmptyNote>Not enough weeks with data yet to draw a trend for {series.name}.</EmptyNote>;
    }

    const n = series.points.length;
    const labelAt = n <= 6 ? series.points.map((_, i) => i) : [0, Math.floor((n - 1) / 2), n - 1];

    return (
        <Box sx={{ display: 'flex', gap: 1 }}>
            <Stack justifyContent="space-between" sx={{ height, py: '4px', width: 30, flexShrink: 0 }}>
                <Typography variant="caption" color="text.secondary">{drawn.max}%</Typography>
                <Typography variant="caption" color="text.secondary">{drawn.min}%</Typography>
            </Stack>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ position: 'relative', height }}>
                    <svg
                        viewBox={`0 0 100 ${height}`}
                        preserveAspectRatio="none"
                        width="100%"
                        height={height}
                        role="img"
                        aria-label={`Weekly accuracy, ${series.name}`}
                        style={{ display: 'block' }}
                    >
                        <line x1="0" x2="100" y1={pad.top} y2={pad.top}
                            stroke={theme.palette.divider} strokeDasharray="2 3"
                            vectorEffect="non-scaling-stroke" />
                        <line x1="0" x2="100" y1={height - pad.bottom} y2={height - pad.bottom}
                            stroke={theme.palette.divider} vectorEffect="non-scaling-stroke" />
                        <polygon points={drawn.area} fill={alpha(color, 0.12)} />
                        <polyline
                            points={drawn.line}
                            fill="none"
                            stroke={color}
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            vectorEffect="non-scaling-stroke"
                        />
                    </svg>
                    {drawn.dots.map(d => (
                        <Tooltip
                            key={d.week}
                            arrow
                            title={`Week of ${shortWeek(d.week)}: ${d.pct}% · ${d.answered.toLocaleString()} answers`}
                        >
                            <Box
                                sx={{
                                    position: 'absolute',
                                    left: `${d.x}%`,
                                    top: d.y,
                                    width: 10,
                                    height: 10,
                                    ml: '-5px',
                                    mt: '-5px',
                                    borderRadius: '50%',
                                    bgcolor: 'background.paper',
                                    border: `2px solid ${color}`,
                                    cursor: 'default',
                                    '&:hover': { bgcolor: color, transform: 'scale(1.3)' },
                                }}
                            />
                        </Tooltip>
                    ))}
                </Box>
                <Box sx={{ position: 'relative', height: 18, mt: 0.5 }}>
                    {labelAt.map(i => (
                        <Typography
                            key={i}
                            variant="caption"
                            color="text.secondary"
                            sx={{
                                position: 'absolute',
                                left: `${drawn.xPct(i)}%`,
                                transform:
                                    i === 0 ? 'none' : i === n - 1 ? 'translateX(-100%)' : 'translateX(-50%)',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {shortWeek(series.points[i].week)}
                        </Typography>
                    ))}
                </Box>
            </Box>
        </Box>
    );
};

/** A tiny line for the breakdown rows. No axes; the numbers beside it carry those. */
const MiniLine = ({ points, color }: { points: Point[]; color: string }) => {
    const valid = points.map((p, i) => ({ p: p.pct, i })).filter(v => v.p !== null) as Array<{ p: number; i: number }>;
    if (valid.length < 2) {
        return <Typography variant="caption" color="text.secondary">—</Typography>;
    }
    const h = 22;
    const vals = valid.map(v => v.p);
    const min = Math.min(...vals);
    const span = Math.max(...vals) - min || 1;
    const step = 100 / Math.max(1, points.length - 1);
    const line = valid.map(v => `${(v.i * step).toFixed(2)},${(h - 3 - ((v.p - min) / span) * (h - 6)).toFixed(2)}`).join(' ');
    return (
        <svg viewBox={`0 0 100 ${h}`} preserveAspectRatio="none" width="100%" height={h} aria-hidden>
            <polyline points={line} fill="none" stroke={color} strokeWidth={1.75}
                strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
        </svg>
    );
};

const BreakdownRow = ({
    series,
    selected,
    onClick,
}: {
    series: Series;
    selected: boolean;
    onClick: () => void;
}) => {
    const theme = useTheme();
    const tone = series.change === null ? 'text.secondary' : series.change >= 0 ? 'success.main' : 'error.main';
    const lineColor =
        series.change === null
            ? theme.palette.text.secondary
            : series.change >= 0
            ? theme.palette.success.main
            : theme.palette.error.main;

    return (
        <ButtonBase
            onClick={onClick}
            sx={{
                width: '100%',
                display: 'grid',
                gridTemplateColumns: 'minmax(90px, 1.2fr) minmax(60px, 1fr) 52px 70px',
                alignItems: 'center',
                columnGap: 1.5,
                px: 1,
                py: 0.75,
                borderRadius: 1.5,
                textAlign: 'left',
                bgcolor: selected ? alpha(theme.palette.success.main, 0.1) : 'transparent',
                outline: selected ? `1px solid ${alpha(theme.palette.success.main, 0.4)}` : 'none',
                '&:hover': { bgcolor: alpha(theme.palette.success.main, selected ? 0.14 : 0.06) },
            }}
        >
            <Typography variant="body2" noWrap sx={{ fontWeight: selected ? 700 : 500 }}>
                {series.name}
            </Typography>
            <MiniLine points={series.points} color={lineColor} />
            <Typography variant="body2" align="right" sx={{ fontWeight: 700 }}>
                {series.latest === null ? '—' : `${Math.round(series.latest)}%`}
            </Typography>
            <Typography variant="caption" align="right" sx={{ fontWeight: 700, color: tone }}>
                {series.change === null ? 'too little data' : changeLabel(series.change)}
            </Typography>
        </ButtonBase>
    );
};

export const AcademicImpact = ({
    widgets,
    sections,
}: {
    widgets?: FilledWidget[];
    sections: SectionRow[];
}) => {
    const widget = findWidget(widgets, 'academic_impact');
    const [split, setSplit] = useState<Split>('subject');
    const [selected, setSelected] = useState<string | null>(null);

    const { school, bySubject, bySection } = useMemo(() => {
        const schoolRows = widgetRows(widgets, 'academic_impact');
        const subjectRows = widgetRows(widgets, 'academic_impact_by_subject');
        const sectionRows = widgetRows(widgets, 'academic_impact_by_section');

        const weeks = Array.from(
            new Set([...schoolRows, ...subjectRows, ...sectionRows].map(r => String(r.week_start)))
        ).sort();

        const group = (rows: WidgetRow[], idKey: string, nameOf: (r: WidgetRow) => string) => {
            const groups = new Map<string, WidgetRow[]>();
            rows.forEach(r => {
                const id = String(r[idKey]);
                groups.set(id, [...(groups.get(id) || []), r]);
            });
            return Array.from(groups.entries())
                .map(([id, rs]) => buildSeries(`${idKey}:${id}`, nameOf(rs[0]), rs, weeks))
                // Weakest now first: the one a principal should look at.
                .sort((a, b) => (a.latest ?? 101) - (b.latest ?? 101));
        };

        const sectionName = new Map(sections.map(s => [String(s.id), s.label]));
        return {
            school: buildSeries('school', 'Whole school', schoolRows, weeks),
            bySubject: group(subjectRows, 'subject_id', r =>
                String(r.subject_name || `Subject ${r.subject_id}`)
            ),
            bySection: group(sectionRows, 'class_section_id', r =>
                sectionName.get(String(r.class_section_id)) || `Section ${r.class_section_id}`
            ),
        };
    }, [widgets, sections]);

    const list = split === 'subject' ? bySubject : bySection;
    const shown = [...bySubject, ...bySection].find(s => s.key === selected) || school;
    const isSchool = shown.key === 'school';
    const measuredWeeks = shown.points.filter(p => p.pct !== null).length;

    const pick = (key: string) => setSelected(prev => (prev === key ? null : key));

    return (
        <Panel
            title="Academic impact"
            tone="success"
            icon={<ShowChartIcon fontSize="small" />}
            subtitle={
                widget?.available
                    ? 'Accuracy on assigned work, week by week. Pick a subject or section to see its own line.'
                    : widget?.reason || 'Waiting on attempt data.'
            }
            action={
                shown.change !== null ? (
                    <Tooltip title={`Latest week against the first measured week${isSchool ? '' : `, ${shown.name}`}`}>
                        <Chip
                            size="small"
                            color={shown.change >= 0 ? 'success' : 'error'}
                            label={changeLabel(shown.change)}
                            sx={{ fontWeight: 700 }}
                        />
                    </Tooltip>
                ) : undefined
            }
        >
            {school.latest === null ? (
                <EmptyNote>
                    No week in the last twelve has enough answered questions to report an
                    accuracy. Shown as absent rather than as zero.
                </EmptyNote>
            ) : (
                <>
                    <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap" sx={{ mb: 1 }}>
                        <Typography variant="h4" sx={{ fontWeight: 700 }}>
                            {shown.latest === null ? '—' : `${shown.latest}%`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {shown.name} · latest week · {measuredWeeks} weeks measured
                        </Typography>
                        {!isSchool && (
                            <Link
                                component="button"
                                variant="caption"
                                onClick={() => setSelected(null)}
                                sx={{ fontWeight: 700 }}
                            >
                                Back to whole school
                            </Link>
                        )}
                    </Stack>

                    <TrendChart series={shown} />

                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={1}
                        sx={{ mt: 2, mb: 0.75 }}
                    >
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            What moved it
                        </Typography>
                        <ToggleButtonGroup
                            size="small"
                            exclusive
                            value={split}
                            onChange={(_, v) => v && setSplit(v)}
                        >
                            <ToggleButton value="subject" sx={{ px: 1.25, py: 0.25 }}>By subject</ToggleButton>
                            <ToggleButton value="section" sx={{ px: 1.25, py: 0.25 }}>By section</ToggleButton>
                        </ToggleButtonGroup>
                    </Stack>

                    {list.length === 0 ? (
                        <EmptyNote>No {split} has answered work in these weeks yet.</EmptyNote>
                    ) : (
                        <Stack spacing={0.25}>
                            {list.map(s => (
                                <BreakdownRow
                                    key={s.key}
                                    series={s}
                                    selected={s.key === selected}
                                    onClick={() => pick(s.key)}
                                />
                            ))}
                        </Stack>
                    )}
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        Weakest first. Change is the latest week against the first week with at
                        least {MIN_WEEK_ANSWERED} answers. Hover a dot on the chart for that week's figures.
                    </Typography>
                </>
            )}
        </Panel>
    );
};

export default AcademicImpact;
